import { lookup } from "node:dns/promises";
import { isIP } from "node:net";

const METADATA_HOSTS = new Set([
	"instance-data",
	"metadata",
	"metadata.azure.internal",
	"metadata.google.internal",
	"metadata.packet.net",
	"metadata.tencentyun.com",
]);

const ALWAYS_BLOCKED_METADATA_ADDRESSES = new Set([
	"100.100.100.200",
	"168.63.129.16",
	"169.254.169.254",
	"169.254.170.2",
	"fd00:ec2::254",
]);
const MAX_BROWSER_URL_LENGTH = 4096;

export class BrowserPolicyError extends Error {
	readonly code = "BROWSER_NETWORK_BLOCKED";

	constructor(message: string) {
		super(message);
		this.name = "BrowserPolicyError";
	}
}

function parseAllowedOrigin(raw: string): string {
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		throw new TypeError(
			`Invalid browser allowed origin: ${JSON.stringify(raw)}`,
		);
	}
	if (url.protocol !== "http:" && url.protocol !== "https:") {
		throw new TypeError(
			`Browser allowed origin must use http or https: ${JSON.stringify(raw)}`,
		);
	}
	if (
		url.username ||
		url.password ||
		url.pathname !== "/" ||
		url.search ||
		url.hash
	) {
		throw new TypeError(
			`Browser allowed origin must be an exact origin without credentials, path, query, or fragment: ${JSON.stringify(raw)}`,
		);
	}
	return url.origin;
}

function ipv4Number(address: string): number | null {
	if (isIP(address) !== 4) return null;
	const parts = address.split(".").map(Number);
	return (
		(((parts[0] ?? 0) << 24) >>> 0) +
		((parts[1] ?? 0) << 16) +
		((parts[2] ?? 0) << 8) +
		(parts[3] ?? 0)
	);
}

function inV4Cidr(address: number, base: number, prefix: number): boolean {
	const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
	return (address & mask) === (base & mask);
}

function isBlockedIpv4(address: string): boolean {
	const value = ipv4Number(address);
	if (value === null) return true;
	return [
		["0.0.0.0", 8],
		["10.0.0.0", 8],
		["100.64.0.0", 10],
		["127.0.0.0", 8],
		["169.254.0.0", 16],
		["172.16.0.0", 12],
		["192.0.0.0", 24],
		["192.0.2.0", 24],
		["192.168.0.0", 16],
		["198.18.0.0", 15],
		["198.51.100.0", 24],
		["203.0.113.0", 24],
		["224.0.0.0", 4],
		["240.0.0.0", 4],
	].some(([base, prefix]) =>
		inV4Cidr(value, ipv4Number(base as string) ?? 0, prefix as number),
	);
}

function parseIpv6(address: string): number[] | null {
	let source = address.toLowerCase();
	const zone = source.indexOf("%");
	if (zone >= 0) source = source.slice(0, zone);

	let ipv4Tail: number[] = [];
	if (source.includes(".")) {
		const lastColon = source.lastIndexOf(":");
		if (lastColon < 0) return null;
		const tail = source.slice(lastColon + 1);
		const v4 = ipv4Number(tail);
		if (v4 === null) return null;
		ipv4Tail = [(v4 >>> 16) & 0xffff, v4 & 0xffff];
		source = source.slice(0, lastColon);
	}

	const halves = source.split("::");
	if (halves.length > 2) return null;
	const parseHalf = (half: string) => {
		if (!half) return [];
		const values = half.split(":").map((part) => Number.parseInt(part, 16));
		return values.every(
			(value) => Number.isInteger(value) && value >= 0 && value <= 0xffff,
		)
			? values
			: null;
	};
	const left = parseHalf(halves[0] ?? "");
	const right = parseHalf(halves[1] ?? "");
	if (!left || !right) return null;
	const present = left.length + right.length + ipv4Tail.length;
	const missing = 8 - present;
	if ((halves.length === 1 && missing !== 0) || missing < 0) return null;
	const fill = halves.length === 2 ? Array(missing).fill(0) : [];
	const result = [...left, ...fill, ...right, ...ipv4Tail];
	return result.length === 8 ? result : null;
}

function isBlockedIpv6(address: string): boolean {
	const words = parseIpv6(address);
	if (!words) return true;
	const [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, g = 0, h = 0] = words;

	// IPv4-mapped IPv6 (::ffff:a.b.c.d) inherits the IPv4 policy.
	if (a === 0 && b === 0 && c === 0 && d === 0 && e === 0 && f === 0xffff) {
		return isBlockedIpv4(`${g >>> 8}.${g & 0xff}.${h >>> 8}.${h & 0xff}`);
	}
	// Unspecified, loopback, and deprecated IPv4-compatible forms.
	if (a === 0 && b === 0 && c === 0 && d === 0 && e === 0 && f === 0) {
		return true;
	}
	// Unique local fc00::/7, link local fe80::/10, multicast ff00::/8.
	if (
		(a & 0xfe00) === 0xfc00 ||
		(a & 0xffc0) === 0xfe80 ||
		(a & 0xff00) === 0xff00
	) {
		return true;
	}
	// Documentation-only 2001:db8::/32.
	if (a === 0x2001 && b === 0x0db8) return true;
	// IETF special-purpose space, 6to4, and documentation-only 3fff::/20.
	// A browser agent should use ordinary global-unicast destinations only;
	// tunneled forms can otherwise encode a private IPv4 endpoint.
	if (a === 0x2001 && b < 0x0200) return true;
	if (a === 0x2002) return true;
	if ((a & 0xfff0) === 0x3ff0) return true;
	if ((a & 0xe000) !== 0x2000) return true;
	return false;
}

export function isPublicAddress(address: string): boolean {
	const family = isIP(address);
	if (family === 4) return !isBlockedIpv4(address);
	if (family === 6) return !isBlockedIpv6(address);
	return false;
}

export class BrowserNetworkPolicy {
	readonly allowedOrigins: ReadonlySet<string>;

	constructor(origins: readonly string[] = []) {
		this.allowedOrigins = new Set(origins.map(parseAllowedOrigin));
	}

	async check(raw: string, base?: string): Promise<URL> {
		let url: URL;
		try {
			url = base ? new URL(raw, base) : new URL(raw);
		} catch {
			throw new BrowserPolicyError(`Invalid URL: ${JSON.stringify(raw)}`);
		}
		if (url.href.length > MAX_BROWSER_URL_LENGTH) {
			throw new BrowserPolicyError(
				`Blocked browser URL longer than ${MAX_BROWSER_URL_LENGTH} characters`,
			);
		}
		if (url.protocol !== "http:" && url.protocol !== "https:") {
			throw new BrowserPolicyError(
				`Blocked non-HTTP browser URL scheme ${JSON.stringify(url.protocol)}`,
			);
		}
		if (url.username || url.password) {
			throw new BrowserPolicyError(
				"Blocked browser URL containing credentials",
			);
		}
		const hostname = url.hostname
			.toLowerCase()
			.replace(/^\[|\]$/g, "")
			.replace(/\.$/, "");
		if (
			hostname === "localhost" ||
			hostname.endsWith(".localhost") ||
			METADATA_HOSTS.has(hostname) ||
			ALWAYS_BLOCKED_METADATA_ADDRESSES.has(hostname)
		) {
			if (
				this.allowedOrigins.has(url.origin) &&
				(hostname === "localhost" || hostname.endsWith(".localhost"))
			) {
				return url;
			}
			throw new BrowserPolicyError(
				`Blocked non-public browser host ${JSON.stringify(hostname)}`,
			);
		}
		if (this.allowedOrigins.has(url.origin)) return url;

		const directFamily = isIP(hostname);
		if (directFamily) {
			if (!isPublicAddress(hostname)) {
				throw new BrowserPolicyError(
					`Blocked non-public browser address ${JSON.stringify(hostname)}`,
				);
			}
			return url;
		}

		let addresses: Array<{ address: string; family: number }>;
		try {
			addresses = await lookup(hostname, { all: true, verbatim: true });
		} catch {
			throw new BrowserPolicyError(
				`Could not safely resolve browser host ${JSON.stringify(hostname)}`,
			);
		}
		if (
			!addresses.length ||
			addresses.some(({ address }) => !isPublicAddress(address))
		) {
			throw new BrowserPolicyError(
				`Blocked browser host ${JSON.stringify(hostname)} because it resolves to a non-public address`,
			);
		}
		return url;
	}
}
